import React, {FC, useCallback, useContext, useEffect, useRef, useState} from "react";
import {Button, Modal} from "react-bootstrap";
import toast from "react-hot-toast";
import {AbuseCheckStatus, AbuseRuleCheck, AbuseWord, abuseWordsService} from "services/abuseWordsService";
import {EnvSetup} from "enums/Envs";
import {toDate} from "services";
import {AuthContext} from "contexts";
import {toDateString} from "utils";
import {Apps} from "enums";
import {AbuseCheckStatusLabels} from "pages/AbuseWords/components/AbuseCheckStatusLabels";


const formatDurationMs = (ms: number): string => {
    if (!Number.isFinite(ms) || ms < 0) {
        return '—';
    }
    const totalSec = Math.floor(ms / 1000);
    const sec = totalSec % 60;
    const totalMin = Math.floor(totalSec / 60);
    const min = totalMin % 60;
    const totalHr = Math.floor(totalMin / 60);
    const hr = totalHr % 24;
    const days = Math.floor(totalHr / 24);
    if (days > 0) {
        return `${days}д ${hr}ч ${min}м`;
    }
    if (totalHr > 0) {
        return `${totalHr}ч ${min}м ${sec}с`;
    }
    if (totalMin > 0) {
        return `${totalMin}м ${sec}с`;
    }
    return `${sec}с`;
};

const getCheckDuration = (result: AbuseRuleCheck): string => {
    const start = new Date(result.startedDate).getTime();
    if (!Number.isFinite(start)) {
        return '—';
    }
    const terminal = +result.status === AbuseCheckStatus.Completed
        || +result.status === AbuseCheckStatus.Cancelled
        || +result.status === AbuseCheckStatus.Failed;
    const end = result.completedDate
        ? new Date(result.completedDate).getTime()
        : terminal
            ? NaN
            : Date.now();
    if (!Number.isFinite(end)) {
        return '—';
    }
    return formatDurationMs(end - start);
};

type Props = {
    ruleId: number | null;
    word: AbuseWord | null;
    env: EnvSetup | undefined;
    onHide: () => void;
    onReloadWords: () => Promise<void>;
};

export const AbuseRuleCheckModal: FC<Props>
    = ({
           ruleId,
           word,
           env,
           onHide,
           onReloadWords,
       }) => {
    const show = !!ruleId && !!word && !!env;
    const {app} = useContext(AuthContext);

    const [activeCheck, setActiveCheck] = useState<AbuseRuleCheck | null | undefined>(undefined);
    const [working, setWorking] = useState(false);
    const [result, setResult] = useState<AbuseRuleCheck | null>(null);

    const wordRef = useRef(word);
    wordRef.current = word;

    const resetTransient = useCallback(() => {
        setResult(null);
    }, []);

    const fetchActiveCheck = useCallback(async () => {
        if (!env || !ruleId) {
            return;
        }
        try {
            const c = await abuseWordsService.getCheck(env, {ruleId});
            setActiveCheck(c);
        } catch {
            setActiveCheck(wordRef.current?.activeCheck ?? null);
        }
    }, [env, ruleId]);

    const handleClose = () => {
        resetTransient();
        onHide();
    };

    const runWithBusy = async (fn: () => Promise<void>, shouldReload?: boolean) => {
        if (!env || !ruleId || !word) {
            return;
        }
        setWorking(true);
        try {
            await fn();
            if (shouldReload) {
                await onReloadWords();
            }
            await fetchActiveCheck();
        } catch {
            // Ошибка уже показана через toast в services/request
        } finally {
            setWorking(false);
        }
    };

    const canCancelActiveCheck = !!activeCheck?.checkId
        && (+activeCheck.status === AbuseCheckStatus.Pending || +activeCheck.status === AbuseCheckStatus.InProgress);

    const handlePrimary = () => {
        void runWithBusy(async () => {
            if (!env || !ruleId) {
                return;
            }
            const checkId = activeCheck?.checkId;
            const res = checkId
                ? await abuseWordsService.getCheck(env, {checkId})
                : await abuseWordsService.startCheck(ruleId, env);
            if (res) {
                setResult(res);
            }
        }, false);
    };

    const handleSecondary = () => {
        void runWithBusy(async () => {
            if (!env || !ruleId) {
                return;
            }
            if (activeCheck?.checkId && canCancelActiveCheck) {
                await abuseWordsService.cancelCheck(activeCheck.checkId, env);
                toast.success('Проверка отменена');
                setResult(null);
                return;
            }
            if (activeCheck?.checkId) {
                const res = await abuseWordsService.getCheck(env, {checkId: activeCheck.checkId});
                if (res) {
                    setResult(res);
                }
                return;
            }
            const res = await abuseWordsService.getCheck(env, {ruleId});
            if (res) {
                setResult(res);
            }
        }, true);
    };

    useEffect(() => {
        if (!show) {
            setActiveCheck(undefined);
            resetTransient();
            return;
        }
        resetTransient();
        void fetchActiveCheck();
    }, [show, fetchActiveCheck, resetTransient]);


    return (
        <Modal show={show} onHide={handleClose} backdrop={working ? 'static' : true} key={ruleId ?? 'closed'}>
            <Modal.Header closeButton>
                <Modal.Title>Проверка правила</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {word && (
                    <>
                        <div><span className="fw-bold">Название:</span> {word.ruleName}</div>
                        <div className="form-control-plaintext"><span className="fw-bold">Паттерн:</span> {word.pattern}
                        </div>
                        <div><span className="fw-bold">Активная проверка:{' '}</span>
                            {activeCheck
                                ? `#${activeCheck.checkId} (${AbuseCheckStatusLabels[activeCheck.status]})`
                                : 'нет'}
                        </div>
                    </>
                )}

                {result && (
                    <div className="border rounded p-3 bg-light my-3">
                        <div className="fw-bold mb-2">
                            {+result.status === AbuseCheckStatus.Completed ? 'Проверка завершена' : 'Статус проверки'}
                        </div>
                        <div><span className="fw-bold">Статус:</span> {AbuseCheckStatusLabels[result.status]}</div>
                        <div><span className="fw-bold">Проверено:</span> {result.processedCount}/{result.totalCount}
                        </div>
                        <div><span className="fw-bold">Совпадений:</span> {result.matchedCount}/{result.totalCount}
                        </div>
                        <div><span className="fw-bold">Прогресс:</span> {result.progressPercent}%</div>
                        <div>
                            <span className="fw-bold">Запущено:</span>
                            {toDateString(toDate(result.startedDate, app ?? Apps.Instinct))}
                        </div>
                        <div>
                            <span className="fw-bold">Длительность:</span>
                            {' '}
                            {getCheckDuration(result)}
                            {(+result.status === AbuseCheckStatus.Pending || +result.status === AbuseCheckStatus.InProgress)
                            && !result.completedDate
                                ? ' (в процессе)'
                                : ''}
                        </div>
                        {result.errorMessage &&
                            <div><span className="fw-bold">Ошибки:</span>
                                <div>{result.errorMessage}</div>
                            </div>
                        }
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose} disabled={working}>
                    Закрыть
                </Button>
                {activeCheck?.checkId &&
                    <Button
                        variant={canCancelActiveCheck ? 'outline-danger' : 'outline-secondary'}
                        onClick={handleSecondary}
                        disabled={working}
                    >
                        {canCancelActiveCheck ? 'Отменить проверку' : 'Показать статус'}
                    </Button>}
                <Button variant="primary" onClick={handlePrimary} disabled={working}>
                    {activeCheck?.checkId ? 'Обновить статус' : 'Запустить проверку'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
